/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import {Root} from "./root";
import {execute, serializeStrategy} from "./strategy_factory";
import {Origin, StrategyType} from "./abstract_stragegy";
import {sendTokenRequest} from "./token_services"
import {v4 as uuid} from 'uuid';

import {Connection, Ping, Pong} from "./connection";

import {initLocalEncryptionCache} from "./encryption_utils";

const Cookies = require("js-cookie");

export const data = new Root(undefined, '', '');
export let connection = new Connection();

export let isReceived = false;
let requested = false;
let ws: WebSocket;
let pingIntervalId: NodeJS.Timer | undefined;

const doNothing = () => {

}

const sendPing = (callback: Function) => {
    executeAndSend(doNothing, Ping, uuid());
    setTimeout(() => {
        // check if last ping has been aknowledged by a pong
        // if not, set app as disconnected
        connection.computeIsConnected();
        callback(connection.clone());
    }, connection.pingAcknowledgeDelay);
};

const startPingLoop = (callback: Function) => {
    if (!pingIntervalId) {
        pingIntervalId = setInterval(() => {
            sendPing(callback);
        }, connection.pingIntervalDelay);
    }
};

export function disconnectEventSource(connCallback: Function, dataCallback: Function): void {
    requested = false;
    clearInterval(pingIntervalId);
    pingIntervalId = undefined;
    if (ws.OPEN) {
        console.info("Closing Websocket");
        ws.close();
    }
    connection.isConnected = false;
    connCallback(connection.clone());
    data.reset();
    dataCallback(data.clone());
}

export function executeAndSend(callback: Function, strategy: StrategyType, ...params: string[]): void {
    if (strategy !== Ping && strategy !== Pong) {
        ++data.lastSeq;
    }
    let date = new Date();
    strategy(Origin.local, data, connection, data.lastSeq, date, data.user, ...params);

    serializeStrategy(data, date, strategy, params)
        .then(msg => {
            ws.send(msg);
            if (strategy !== Ping && strategy !== Pong) {
                callback(data.clone());
            }
        })
    ;
}

export function initEventSourceFromCookies(dataCallback: Function, connCallback: Function) {
    let token = Cookies.get('fw-utoken');
    let authType = Cookies.get('fw-uauthtype');

    if (token && authType) {
        if(authType == 'google') {
            sendTokenRequest().then(response => {
                let tokens = response[0];
                let decodedIdToken = response[1];

                let avatar;
                if (decodedIdToken.picture) {
                    avatar = decodedIdToken.picture;
                } else {
                    avatar = Cookies.get('fw-uavatar');
                }

                data.userAvatar = avatar;
                dataCallback(data.clone());

                initEventSource(dataCallback,
                    connCallback,
                    decodedIdToken.email,
                    tokens.id_token,
                    authType,
                    false);
            })
            .catch((error => {
                console.error("Token request error", error);
            }));
        }
        else if(authType == 'basic') {
            let username = Cookies.get('fw-uname');
            initEventSource(dataCallback,
                connCallback,
                username,
                token,
                'basic',
                false);
        }
    }
}

function getWebsocketUri() {
    let protocol;
    if (window.location.protocol == 'http:') {
        protocol = 'ws:';
    } else {
        protocol = 'wss:';
    }
    const host = `${protocol}//${window.location.hostname}`;
    const port = window.location.port;
    if (port) {
        return `${host}:${port}/ws`;
    } else {
        return `${host}/ws`;
    }
}

export function resetEventSourceData(dataCallback: Function) {
    if (ws.OPEN) {
        data.reset();
        dataCallback(data.clone());

        ws.send(`2, 2000-01-01 00:00:00.0+00:00, admin@local.host: Replay("0", "")`);
    }
}

export function initEventSource(dataCallback: Function,
                                connCallback: Function,
                                username: string,
                                token: string,
                                authtype: string,
                                autocreate: boolean) {
    console.info('Initializing event source', username, token, authtype, autocreate, requested);

    initLocalEncryptionCache();

    if (requested) {    // Make sure we only run it once
        return;
    }
    requested = true;
    if ("WebSocket" in window) {
        ws = new WebSocket(getWebsocketUri());

        ws.onopen = function () {
            console.info("Connection is opened");
            ws.send(`1, 2000-01-01 00:00:00.0+00:00, admin@local.host: Authenticate("${username}", "${authtype}|${token}", "${autocreate}")`);
            sendPing(connCallback);
            ws.send(`2, 2000-01-01 00:00:00.0+00:00, admin@local.host: Replay("0", "")`);
            startPingLoop(connCallback);
        };

        ws.onmessage = function (evt) {
            const received_msg = evt.data.toString();
            // console.log('ws.onmessage received_msg', received_msg);
            received_msg.split('\n').forEach((s: string) => {
                execute(data, connection, s)
                    .then(response => {
                        if(response || isReceived) {
                            isReceived = true;
                            if (username && !data.username) {
                                data.username = username;
                            }
                            dataCallback(data.clone());
                        }
                    });
            });
        };

        ws.onclose = function () {
            console.warn("Websocket is closed");
            disconnectEventSource(connCallback, dataCallback);
            setTimeout(initEventSourceFromCookies, connection.reconnectDelay, connCallback, dataCallback);
        };
    } else {
        console.error("WebSocket is not supported by your Browser");
    }
}

